<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
    <div class="report">
        <!-- Basic info -->
        <h3 class="pl-1">
            {{ $t('area_report_details.title') }}
        </h3>
        <table class="table table-sm">
            <tbody>
            <tr>
                <td class="item-detail-title">
                    {{ $t('area_report.created') }}
                </td>
                <td class="item-detail-text">
                    {{ toLocalTime(reportDetails.completed_time) }}
                </td>
            </tr>

            <tr>
                <td class="item-detail-title">
                    {{ $t('area_report_details.inspector') }}
                </td>
                <td class="item-detail-text">
                    {{ reportDetails.reporter.last_name }}, {{ reportDetails.reporter.first_name }}
                </td>
            </tr>
            <tr>
                <td class="item-detail-title">
                    {{ $t('area_report_details.area_name') }}
                </td>
                <td class="item-detail-text">
                    <span>{{ reportDetails.area.name }}</span>
                </td>
            </tr>

            <tr>
                <td class="item-detail-title">
                    {{ $t('area_report_details.area_type') }}
                </td>
                <td class="item-detail-text">
                    <span>{{ reportDetails.area.type.name }}</span>
                </td>
            </tr>

            <tr>
                <td class="item-detail-title">
                    {{ $t('area_report_details.note') }}
                </td>
                <td
                    v-if="reportDetails.note"
                    class="item-detail-text"
                >
                    {{ reportDetails.note }}
                </td>
                <td
                    v-else
                    class="item-detail-text"
                >
                    -
                </td>
            </tr>
            <tr>
                <td class="item-detail-title">
                    {{ $t('area_report.status') }}
                </td>
                <td class="item-detail-text">
                    {{ reportDetails.status === 1 ? $t('area_report.status_open') : $t('area_report.completed') }}
                </td>
            </tr>
            </tbody>
        </table>

        <!--Inspection results-->
        <span class="span-header">{{ $t('area_report_details.equipment_inspection_results') }}</span>

        <div class="table-responsive">
            <table class="table table-sm">
                <thead>
                <tr>
                    <!-- Add headers dynamically -->
                    <th v-for="field in fields" :key="field.key">{{ field.label }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in reportDetails.equipment_inspection_results" :key="row.id">
                    <td>
                        {{ row.equipment && row.equipment.name ? row.equipment.name : '' }}
                    </td>
                    <td>
                        {{ row.equipment && row.equipment.manufacturer ? row.equipment.manufacturer : '' }}
                    </td>
                    <td>
                        {{ row.value ? row.value : '' }}
                    </td>
                    <td>
                        {{ row.info ? row.info : '' }}
                    </td>
                    <td>
                      <span :class="{
                        'text-success': row.result === resultOk,
                        'text-warning': row.result === resultDefect,
                        'text-danger': row.result === resultBan
                      }">
                        {{ getEquipmentResult(row.result) }}
                      </span>
                    </td>
                    <td>
                        <div class="text-right" style="height: 15em;">
                            <ImageView
                                v-for="photo in row.photos"
                                :key="photo.id"
                                ref="image"
                                :contain="true"
                                class="flicking-panel"
                                :url="getReportPhotoUrl(row.id, photo)"
                                :cacheable-photo-id="photo.id"
                                :cache-photo="true"
                                :control-size="'sm'"
                                @click="openImage(row.id, photo)"
                            />
                        </div>
                    </td>
                    <!-- Add more cells if needed -->
                </tr>
                </tbody>
            </table>
        </div>

        <div class="button-container">
            <button
                class="btn btn-secondary form-button"
                @click.stop="$emit('closeAndUpdate')"
            >
                <span>{{ $t('common.close') }}</span>
            </button>
            <button
                class="btn btn-primary form-button"
                @click.stop="generatePdf"
            >
                <span>{{ $t('common.print') }}</span>
            </button>
        </div>


        <Transition name="fade">
            <fullscreen-image
                v-if="showPhoto"
                :url="getReportPhotoUrl(itemId, photo)"
                @close="closePhoto"
            />
        </Transition>
        <!-- Loading Spinner -->
        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />

        <!-- Modal for Deletion Confirmation -->
        <base-modal
            v-if="deletion"
            :hide-footer="true"
            @close="deletion=false"
        >
            <div class="modal-dialog modal-sm modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-body ">
                        <h5 class="modal-title justify-content-center ms-4" id="modalTitle">{{ $t('common.confirm_delete') }}</h5>
                    </div>
                    <div class="modal-footer justify-content-center">
                        <button type="button" class="btn btn-secondary me-2" @click="deletion = false">{{ $t('common.no') }}</button>
                        <button type="button" class="btn btn-danger" @click="deleteReport()">{{ $t('common.yes') }}</button>
                    </div>
                </div>
            </div>
        </base-modal>

    </div>
</template>

<script>
import {areaHelper} from '../../mixins/AreaMixin'
import {areaReportHelper} from '../../mixins/AreaReportMixin'
import {restApi} from '../../mixins/RestApiMixin'
import FullscreenImage from "../../view/FullscreenImage";
import {timeUtils} from "@/components/mixins/TimeUtils";
import ImageView from "@/components/view/ImageView";
import {inject, nextTick} from "vue";
import Vue3Datatable from "@bhplugin/vue3-datatable";
import BaseModal from "@/components/BaseModal.vue";
import {PdfGeneratorManager} from "@/modules/PdfGeneratorManager";
import {imageCacheService} from "@/services/ImageCacheService";
import {pdfMixin} from "@/components/mixins/PdfMixin";

export default {
    name: 'AreaReportDetails',
    components: {BaseModal, Vue3Datatable, ImageView, FullscreenImage},
    mixins: [areaReportHelper, areaHelper, restApi, timeUtils, pdfMixin],
    props: {
        reportDetails: {
            type: Object,
            default: null
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data() {
        return {
            sortBy: 'equipment.name',
            sortDesc: false,
            completed: 2,
            deletion: false,
            itemId: null,
            photo: null,
            showPhoto: false,
            fields: [
                {key: 'equipment.name', label: this.$t('area_report_details.inspected_equipment'), sortable: true, headerClass: 'routa-table-header'},
                {key: 'equipment.manufacturer', label: this.$t('area_report_details.equipment_manufacturer'), sortable: false, headerClass: 'routa-table-header'},
                {key: 'value', label: this.$t('area_report_details.value'), sortable: false, headerClass: 'routa-table-header'},
                {key: 'info', label: this.$t('area_report_details.info'), class: 'equipment-info', sortable: false, headerClass: 'routa-table-header'},
                {key: 'result', label: this.$t('area_report_details.result'), sortable: true, headerClass: 'routa-table-header'},
                {key: 'photos', label: '', class: 'text-end',headerClass: 'routa-table-header' }
            ],
            loading: false
        }
    },
    beforeUnmount() {
        imageCacheService.clear();
    },

    methods: {

        getHeaderTable() {
            return [
                [this.$t('area_report_details.inspector'), this.reportDetails.reporter.first_name + ' ' + this.reportDetails.reporter.last_name],
                [this.$t('area_report_details.area_name'), this.reportDetails.area.name],
                [this.$t('area_report_details.area_type'), this.reportDetails.area.type.name],
                [this.$t('area_report_details.note'), this.reportDetails.note ? this.reportDetails.note : '-'],
                [this.$t('area_report.status'), this.reportDetails.status === 1 ? this.$t('area_report.status_open') : this.$t('area_report.completed')],
                [this.$t('area_report.created'), this.toLocalTime(this.reportDetails.completed_time)]
            ]
        },

        getResultsTable(result) {
            return [
                [this.$t('area_report_details.inspected_equipment'), result.equipment && result.equipment.name ? result.equipment.name : ''],
                [this.$t('area_report_details.equipment_manufacturer'), result.equipment && result.equipment.manufacturer ? result.equipment.manufacturer : ''],
                [this.$t('area_report_details.value'), result.value ? result.equipment.value : ''],
                [this.$t('area_report_details.info'), result.info ? result.info : ''],
                [this.$t('area_report_details.result'), this.getEquipmentResult(result.result)]
            ]
        },

        async generatePdf() {
            this.loading = true
            await nextTick()

            const {
                pdfDoc, pdfService, fonts, colors, footerLogo, companyFooterLogo, pdfGeneratorManager
            } = await this.initializePdf()

            const { fontText, fontBold, fontHeader } = fonts
            const { textColor, headerColor, boldColor } = colors

            let nextYPosition = undefined
            // Document title
            let title = this.$t('ins_wizard.area_inspection')
            nextYPosition -= 10

            nextYPosition = pdfService.addText(pdfDoc, title, fontHeader, 20, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo, headerColor)

            nextYPosition = pdfService.addVerticalTable(this.getHeaderTable(), pdfDoc, {
                textFont: fontText,
                headerFont: fontBold,
                textFontSize: 9,
                headerFontSize: 9,
                textColor: textColor,
                headerColor: boldColor
            }, nextYPosition, undefined, undefined, footerLogo)

            nextYPosition -= 40

            const resultTitle = this.$t('area_report_details.equipment_inspection_results')
            nextYPosition = pdfService.addText(pdfDoc, resultTitle, fontBold, 14, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo, boldColor)

            nextYPosition -= 10

            for (const inspection of this.reportDetails.equipment_inspection_results) {
                nextYPosition -= 20
                const currentPages = pdfDoc.getPages().length
                const available = nextYPosition - pdfService.footerZone - 120
                if (available < 0) {
                    nextYPosition -= 800
                }

                const result = pdfService.addVerticalTable(this.getResultsTable(inspection), pdfDoc, {
                    textFont: fontText,
                    headerFont: fontBold,
                    textFontSize: 9,
                    headerFontSize: 9,
                    textColor: textColor,
                    headerFontColor: boldColor
                }, nextYPosition, undefined, undefined, footerLogo, 5, 1, undefined, 3)

                const newPages = pdfDoc.getPages().length

                //sync components Y position
                if (newPages !== currentPages) {
                    const newPage = pdfDoc.getPage(pdfDoc.getPages().length - 1)
                    nextYPosition = newPage.getHeight() - pdfService.defaultVerticalMargin
                }

                let photoResult = result
                if (inspection.photos && inspection.photos.length > 0) {
                    //Inspection can only have one photo currently
                    const url = this.getReportPhotoUrl(inspection.id, inspection.photos[0])
                    const photoId = inspection.photos[0].id;

                    let photo;

                    if (imageCacheService.has(photoId.toString())) {
                        photo = imageCacheService.get(photoId.toString());
                    } else {
                        photo = (await new Promise(resolve => this.restFetch(url, resolve))).data
                    }

                    if (photo) {
                        let image
                        if (this.isPng(photo)) {
                            image = await pdfDoc.embedPng(photo)
                        } else {
                            image = await pdfDoc.embedJpg(photo)
                        }
                        photoResult = pdfService.addImage(pdfDoc, undefined, image, undefined, nextYPosition -= 10, footerLogo, 80, 5, 4, 2)
                    }
                } else {
                    // TODO - Add text "No photo"
                }
                nextYPosition = Math.min(result, photoResult)
            }

            const pdfBytes = await pdfGeneratorManager.generateBytesFromPdfDoc(pdfDoc)
            PdfGeneratorManager.downloadPdf(pdfBytes, this.$t('ins_report_details.title'))
            this.loading = false
        },

        isPng(photo) {
            return photo.startsWith('data:image/png;');
        },


        confirmDeleteReport: function () {
            if (!this.loading) {
                this.deletion = true
            }
        },

        deleteReport: function () {
            this.deletion = false
            this.loading = true
            var url = this.areaReportUrl + '/' + this.reportDetails.id
            this.restDelete(url, this.success, this.fail)
        },
        success: function () {
            this.loading = false
            this.$emit('closeAndUpdate')
        },

        fail: function () {
            this.loading = false
            this.showAlert(this.$t('common.alert_update_failed'))
        },
        openImage: function (resultId, photo) {
            this.itemId = resultId
            this.photo = photo
            this.showPhoto = true
        },
        closePhoto() {
            this.showPhoto = false
        }
    }
}
</script>
